/**
 * Hotel Booking Review API
 *
 * Handles hotel booking review (HOTEL_REVIEW_MCP) API calls.
 */
import type { HotelReviewTemplateData } from '@contracts';
import type { HotelBookingReviewRequestParams } from '../../types/bookingReviewTypes';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { baseFetch, createApiError } from './baseApi';

// ============================================
// Error Class
// ============================================

export const HotelReviewError = createApiError('HotelReviewError');

// ============================================
// Response Type
// ============================================

export interface HotelReviewResponse {
  type: 'hotels-review';
  data: HotelReviewTemplateData | null;
  success?: boolean;
  error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  type?: string;
  success?: boolean;
  data?: HotelReviewTemplateData | null;
  error?: { code: string; message: string };
}

// ============================================
// API Function
// ============================================

/**
 * Fetch hotel booking review details from the API
 * POST /hotels/booking-review?sessionId=...&userId=...
 *
 * Expects MCP format payload built by the caller (roomSelectionThunks)
 */
export async function fetchHotelBookingReview(
  params: HotelBookingReviewRequestParams,
): Promise<HotelReviewResponse> {
  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.BOOKING_REVIEW,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: params.payload,
    logTag: 'HotelReviewClient',
  });

  // Network/HTTP error
  if (error) {
    return { type: 'hotels-review', success: false, data: null, error };
  }

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelReviewClient] API returned error:', data.error);
    return { type: 'hotels-review', success: false, data: data.data ?? null, error: data.error };
  }

  // Success
  logger.warn('[HotelReviewClient] Success:', { hasData: data?.data !== null });
  return { type: 'hotels-review', success: true, data: data?.data ?? null };
}
